import React, { useState, useEffect, useMemo } from 'react';
import {
    X,
    Download,
    Clock,
    FileText,
    CheckCircle2,
    UserPlus,
    UploadCloud,
    Tag,
    Layers,
    Users,
    Activity,
    ChevronRight
} from 'lucide-react';
import api from '../../../../../services/api';
import { fetchAuditLogs, downloadAuditLogs } from '../../../services/auditService';
import CustomLoader from '../../../../../shared/components/ui/CustomLoader';
import './AuditLogsModal.css';

import { formatLocalDateTime } from '../../../../../utils/date';

// Helper to format date strings nicely
const formatDate = (dateStr) => {
    return formatLocalDateTime(dateStr);
};

// Helper to format metadata fields into readable text
const formatFieldName = (str) => {
    if (!str) return '';
    const cleaned = String(str).trim().replace(/^[.,:;]+|[.,:;]+$/g, '').replace(/[._]/g, ' ');
    const acronyms = {
        rfp: 'RFP',
        id: 'ID',
        pdf: 'PDF',
        url: 'URL',
        be: 'BE',
        fe: 'FE',
        aed: 'AED',
        usd: 'USD'
    };
    return cleaned
        .split(/\s+/)
        .map(word => {
            const lower = word.toLowerCase();
            if (acronyms[lower]) return acronyms[lower];
            return lower.charAt(0).toUpperCase() + lower.slice(1);
        })
        .join(' ');
};

// Helper to format raw audit messages from BE (e.g. converting snake_case fields to clean Title Case and deduplicating them)
const formatAuditMessage = (msg) => {
    if (!msg || typeof msg !== 'string') return msg;

    // Check if the message contains a colon followed by a list of fields (e.g. "...fields: proposal_deadline, ...")
    const colonIndex = msg.indexOf(':');
    if (colonIndex !== -1) {
        const prefix = msg.slice(0, colonIndex + 1);
        let suffix = msg.slice(colonIndex + 1).trim();
        const hasTrailingPeriod = suffix.endsWith('.');
        if (hasTrailingPeriod) {
            suffix = suffix.slice(0, -1).trim();
        }

        const parts = suffix.split(',').map(s => s.trim()).filter(Boolean);
        if (parts.length > 0 && parts.some(p => p.includes('_') || /^[a-zA-Z0-9_]+$/.test(p))) {
            const seen = new Set();
            const formattedParts = [];
            for (const part of parts) {
                const formatted = formatFieldName(part);
                if (formatted && !seen.has(formatted.toLowerCase())) {
                    seen.add(formatted.toLowerCase());
                    formattedParts.push(formatted);
                }
            }
            if (formattedParts.length > 0) {
                return `${prefix} ${formattedParts.join(', ')}${hasTrailingPeriod ? '.' : ''}`;
            }
        }
    }

    // General fallback: replace any standalone snake_case field identifiers (e.g. proposal_deadline -> Proposal Deadline)
    return msg.replace(/\b[a-zA-Z0-9]+(?:_[a-zA-Z0-9]+)+\b/g, (match) => {
        return formatFieldName(match);
    });
};

// Helper to convert UTC timestamp to local browser time
const getLocalTime = (dateGroup, timestampStr) => {
    if (!timestampStr) return '';

    // Normalize dateGroup if it's relative
    let dateBase = dateGroup || '';
    const today = new Date();
    if (dateBase.toLowerCase().includes('today')) {
        dateBase = today.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    } else if (dateBase.toLowerCase().includes('yesterday')) {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        dateBase = yesterday.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    }

    // Remove "UTC" if present and clean up spaces
    const cleanTime = timestampStr.replace(/\s*UTC\s*/i, '').trim();

    // Try to construct a full datetime string in UTC
    const combinedStr = `${dateBase} ${cleanTime} UTC`;
    let localDate = new Date(combinedStr);

    // If the combined date parsing fails, fallback to parsing time relative to today
    if (isNaN(localDate.getTime())) {
        const year = today.getUTCFullYear();
        const month = String(today.getUTCMonth() + 1).padStart(2, '0');
        const day = String(today.getUTCDate()).padStart(2, '0');
        localDate = new Date(`${year}-${month}-${day}T${cleanTime}Z`);
    }

    // If still invalid, return original timestampStr
    if (isNaN(localDate.getTime())) {
        return timestampStr;
    }

    // Format to local browser time
    const formatted = new Intl.DateTimeFormat(undefined, {
        hour: '2-digit',
        minute: '2-digit',

        hour12: true
    }).format(localDate);

    // Normalize AM/PM representation
    return formatted.replace(/([ap]\.?\s?m\.?)/i, (match) => {
        return match.toUpperCase().replace(/\./g, '').replace(/\s/g, '');
    });
};

// Helper component for action icons
const ActionIcon = ({ iconName }) => {
    switch (iconName) {
        case 'file-text':
            return <FileText size={14} />;
        case 'check-square':
        case 'check-circle':
            return <CheckCircle2 size={14} />;
        case 'user-plus':
            return <UserPlus size={14} />;
        case 'upload-cloud':
        case 'file-up':
            return <UploadCloud size={14} />;
        default:
            return <Clock size={14} />;
    }
};

const AuditLogsModal = ({ rfpId, onClose }) => {
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [activeFilter, setActiveFilter] = useState('All');

    useEffect(() => {
        const fetchLogs = async () => {
            try {
                const response = await fetchAuditLogs(rfpId);
                setData(response.data);
            } catch (err) {
                console.error("Failed to fetch audit logs", err);
                setError(err.response?.data?.message || "Failed to load audit logs.");
            } finally {
                setLoading(false);
            }
        };
        fetchLogs();
    }, [rfpId]);

    const handleDownload = async () => {
        try {
            setDownloading(true);
            const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const response = await downloadAuditLogs(rfpId, userTimezone);

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `audit_logs_${rfpId}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Failed to download audit logs pdf", err);
            setError("Failed to download PDF.");
        } finally {
            setDownloading(false);
        }
    };

    // Filter tags definition
    const filters = ['All', 'Uploads', 'Assignments', 'Metadata', 'Submissions'];

    // Group and filter the timeline logs
    const processedTimeline = useMemo(() => {
        if (!data?.timeline) return [];

        return data.timeline.map(group => {
            // Apply filtering to entries
            const filteredEntries = group.entries.filter(entry => {
                if (activeFilter === 'All') return true;
                const key = entry.action?.key;
                if (activeFilter === 'Uploads') return key === 'rfp_uploaded';
                if (activeFilter === 'Assignments') return key === 'section_assigned';
                if (activeFilter === 'Metadata') return key === 'metadata_updated';
                if (activeFilter === 'Submissions') return key === 'final_submitted' || key === 'section_submitted';
                return true;
            });

            // Grouping logic:
            // 1. Group multiple "section_assigned" logs at the same timestamp into a single "Assigned Sections" event.
            // 2. Group consecutive identical events with the same timestamp and action key.
            const groupedEntries = [];

            filteredEntries.forEach(entry => {
                const key = entry.action?.key;

                if (key === 'section_assigned') {
                    // Group section_assigned entries that have the same timestamp and personnel
                    const existingGroup = groupedEntries.find(
                        item => item.action?.key === 'section_assigned' &&
                            item.timestamp === entry.timestamp &&
                            item.personnel?.name === entry.personnel?.name
                    );

                    const assignmentDetail = {
                        section_title: entry.metadata?.section_title ||
                            entry.description?.split('Section: ')?.[1]?.split(' | ')?.[0] ||
                            'Unknown Section',
                        assignee_name: entry.metadata?.assignee_name ||
                            entry.description?.split('Assigned to: ')?.[1] ||
                            'Unknown Assignee',
                        deadline: entry.metadata?.deadline || null,
                        log_id: entry.log_id
                    };

                    if (existingGroup) {
                        existingGroup.assignments.push(assignmentDetail);
                        if (entry.message) {
                            if (!existingGroup.messages) {
                                existingGroup.messages = [existingGroup.message];
                            }
                            if (!existingGroup.messages.includes(entry.message)) {
                                existingGroup.messages.push(entry.message);
                            }
                        }
                    } else {
                        groupedEntries.push({
                            ...entry,
                            action: {
                                ...entry.action,
                                display_name: 'Assigned Sections',
                                badge: {
                                    bg_color: '#F4F4F5',
                                    text_color: '#18181B'
                                }
                            },
                            assignments: [assignmentDetail],
                            messages: entry.message ? [entry.message] : []
                        });
                    }
                } else {
                    // For other events (e.g. metadata_updated), group if consecutive with same timestamp and action key
                    const lastEntry = groupedEntries[groupedEntries.length - 1];
                    if (lastEntry &&
                        lastEntry.action?.key === key &&
                        lastEntry.timestamp === entry.timestamp &&
                        lastEntry.personnel?.name === entry.personnel?.name) {

                        // Append metadata or skip duplicates if they represent identical logs
                        if (!lastEntry.duplicates) {
                            lastEntry.duplicates = [lastEntry.description];
                        }
                        if (!lastEntry.duplicates.includes(entry.description)) {
                            lastEntry.duplicates.push(entry.description);
                        }
                        if (entry.message) {
                            if (!lastEntry.messages) {
                                lastEntry.messages = [lastEntry.message];
                            }
                            if (!lastEntry.messages.includes(entry.message)) {
                                lastEntry.messages.push(entry.message);
                            }
                        }
                    } else {
                        groupedEntries.push({
                            ...entry,
                            messages: entry.message ? [entry.message] : []
                        });
                    }
                }
            });

            return {
                ...group,
                entries: groupedEntries
            };
        }).filter(group => group.entries.length > 0);
    }, [data, activeFilter]);

    // Component to render body based on action keys
    const renderTimelineBody = (entry) => {
        const key = entry.action?.key;

        switch (key) {
            case 'rfp_uploaded': {
                const filename = entry.metadata?.filename || 'Document';
                const messages = entry.messages || (entry.message ? [entry.message] : []);
                return (
                    <div className="audit-card-body-wrapper upload">
                        <div className="audit-readable-desc">
                            {messages.length > 0 ? (
                                messages.map((msg, idx) => (
                                    <p key={idx} style={{ margin: '0 0 4px 0' }}>{msg}</p>
                                ))
                            ) : (
                                <p style={{ margin: 0 }}>
                                    Uploaded and initialized the RFP source document to launch the extraction system.
                                </p>
                            )}
                        </div>
                        <div className="audit-file-badge">
                            <div className="audit-file-icon-box">
                                <UploadCloud size={18} />
                            </div>
                            <div className="audit-file-info">
                                <span className="audit-file-name" title={filename}>{filename}</span>
                                <span className="audit-file-type">Primary PDF</span>
                            </div>
                        </div>
                    </div>
                );
            }

            case 'metadata_updated': {
                const rawFields = entry.metadata?.updated_fields || [];
                const note = entry.metadata?.note || '';
                const messages = entry.messages || (entry.message ? [entry.message] : []);

                // Deduplicate field tags for clean chip display
                const uniqueChips = Array.from(
                    new Set(rawFields.map(f => formatFieldName(f)).filter(Boolean))
                );

                return (
                    <div className="audit-card-body-wrapper metadata">
                        <div className="audit-readable-desc">
                            {messages.length > 0 ? (
                                messages.map((msg, idx) => (
                                    <p key={idx} style={{ margin: '0 0 4px 0' }}>{formatAuditMessage(msg)}</p>
                                ))
                            ) : (
                                <p style={{ margin: 0 }}>
                                    Updated the proposal settings and critical parameters.
                                </p>
                            )}
                        </div>
                        {uniqueChips.length > 0 && (
                            <div className="audit-metadata-chips">
                                {uniqueChips.map((fieldName, index) => (
                                    <span key={index} className="audit-meta-chip">
                                        <Tag size={10} className="audit-chip-icon" />
                                        {fieldName}
                                    </span>
                                ))}
                            </div>
                        )}
                        {note && (
                            <div className="audit-note-box">
                                <span className="audit-note-label">System Note:</span>
                                <span className="audit-note-text">{note}</span>
                            </div>
                        )}
                    </div>
                );
            }

            case 'section_assigned': {
                const list = entry.assignments || [];
                const messages = entry.messages || (entry.message ? [entry.message] : []);
                return (
                    <div className="audit-card-body-wrapper assignment">
                        <div className="audit-readable-desc">
                            {messages.length > 0 ? (
                                messages.map((msg, idx) => (
                                    <p key={idx} style={{ margin: '0 0 4px 0' }}>{msg}</p>
                                ))
                            ) : (
                                <p style={{ margin: 0 }}>
                                    Assigned <strong>{list.length}</strong> {list.length === 1 ? 'section' : 'sections'} to the proposal delivery team.
                                </p>
                            )}
                        </div>
                        <div className="audit-assignment-table">
                            <div className="audit-table-header">
                                <div className="col-section">Section</div>
                                <div className="col-assignee">Assignee</div>
                                <div className="col-deadline">Deadline</div>
                            </div>
                            <div className="audit-table-body">
                                {list.map((item, idx) => (
                                    <div key={idx} className="audit-table-row">
                                        <div className="col-section font-semibold" title={item.section_title}>
                                            {item.section_title}
                                        </div>
                                        <div className="col-assignee">
                                            <div className="audit-user-avatar-mini">
                                                {item.assignee_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                            </div>
                                            <span className="truncate">{item.assignee_name}</span>
                                        </div>
                                        <div className="col-deadline text-muted">
                                            {formatDate(item.deadline)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                );
            }

            case 'final_submitted': {
                const oldStage = entry.metadata?.old_stage || 'RFP In-Progress';
                const messages = entry.messages || (entry.message ? [entry.message] : []);
                return (
                    <div className="audit-card-body-wrapper submission">
                        <div className="audit-readable-desc">
                            {messages.length > 0 ? (
                                messages.map((msg, idx) => (
                                    <p key={idx} style={{ margin: '0 0 4px 0' }}>{msg}</p>
                                ))
                            ) : (
                                <p style={{ margin: 0 }}>
                                    Completed proposal review cycles and submitted final response to the client.
                                </p>
                            )}
                        </div>
                        <div className="audit-stage-transition">
                            <span className="audit-stage-badge prev">{oldStage}</span>
                            <ChevronRight size={14} className="audit-arrow" />
                            <span className="audit-stage-badge current">Submitted to Client</span>
                        </div>
                    </div>
                );
            }

            default: {
                const messages = entry.messages || (entry.message ? [entry.message] : []);
                return (
                    <div className="audit-card-body-wrapper default">
                        <div className="audit-readable-desc">
                            {messages.length > 0 ? (
                                messages.map((msg, idx) => (
                                    <p key={idx} style={{ margin: '0 0 4px 0' }}>{formatAuditMessage(msg)}</p>
                                ))
                            ) : (
                                <p style={{ margin: 0 }}>{formatAuditMessage(entry.description)}</p>
                            )}
                        </div>
                    </div>
                );
            }
        }
    };

    return (
        <div className="trail-modal-overlay" onClick={onClose}>
            <div className="trail-modal-box" onClick={(e) => e.stopPropagation()}>

                {/* Clean Header */}
                <div className="trail-modal-header">
                    <div className="audit-header-title-block">
                        <h2>Audit Trail</h2>
                        <div className="audit-rfp-metadata">
                            <span className="audit-rfp-title" title={data?.summary?.rfp_title}>
                                {data?.summary?.rfp_title || 'Untitled Proposal'}
                            </span>
                            <span className="audit-metadata-bullet">•</span>
                            <span className="audit-client-name" title={data?.summary?.client_name || 'N/A'}>
                                {data?.summary?.client_name || 'N/A'}
                            </span>
                        </div>
                    </div>
                    <div className="audit-header-actions">
                        <button
                            className="audit-download-btn"
                            onClick={handleDownload}
                            disabled={!data || loading || downloading}
                        >
                            {downloading ? <CustomLoader size={14} /> : <Download size={14} />}
                            <span>{downloading ? 'Downloading...' : 'Download PDF'}</span>
                        </button>
                        <button className="audit-close-btn" onClick={onClose}>
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Main Scrollable Content */}
                <div className="trail-modal-content">
                    {loading ? (
                        <div className="audit-loading">
                            <CustomLoader size={26} />
                            <span>Retrieving complete audit logs...</span>
                        </div>
                    ) : error ? (
                        <div className="audit-error">
                            <p>{error}</p>
                        </div>
                    ) : (
                        <>
                            {/* Premium SaaS Summary Dashboard Cards */}
                            {data?.summary && (
                                <div className="audit-summary-grid">
                                    <div className="audit-summary-card">
                                        <div className="audit-card-icon red">
                                            <Activity size={16} />
                                        </div>
                                        <div className="audit-card-info">
                                            <span className="audit-card-val">{data.summary.total_actions ?? 0}</span>
                                            <span className="audit-card-lbl">Total Actions</span>
                                        </div>
                                    </div>
                                    <div className="audit-summary-card">
                                        <div className="audit-card-icon blue">
                                            <Users size={16} />
                                        </div>
                                        <div className="audit-card-info">
                                            <span className="audit-card-val">{data.summary.total_contributors ?? 0}</span>
                                            <span className="audit-card-lbl">Contributors</span>
                                        </div>
                                    </div>
                                    <div className="audit-summary-card">
                                        <div className="audit-card-icon green">
                                            <Layers size={16} />
                                        </div>
                                        <div className="audit-card-info">
                                            <span className="audit-card-val">{data.summary.distinct_events ?? 0}</span>
                                            <span className="audit-card-lbl">Distinct Events</span>
                                        </div>
                                    </div>
                                    <div className="audit-summary-card stage-card">
                                        <div className="audit-card-info">
                                            <span className="audit-card-val truncate" title={data.summary.current_stage}>
                                                {data.summary.current_stage || 'N/A'}
                                            </span>
                                            <span className="audit-card-lbl">Current Stage</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Filter Chips Bar */}
                            <div className="audit-filter-bar">
                                {filters.map(filter => (
                                    <button
                                        key={filter}
                                        className={`audit-filter-chip ${activeFilter === filter ? 'active' : ''}`}
                                        onClick={() => setActiveFilter(filter)}
                                    >
                                        {filter}
                                    </button>
                                ))}
                            </div>

                            {/* Scrollable Timeline Activity Area */}
                            <div className="audit-timeline-activity-area">
                                {processedTimeline.length > 0 ? (
                                    <div className="audit-timeline-container">
                                        <div className="audit-timeline-line"></div>

                                        {processedTimeline.map((group, idx) => (
                                            <div key={idx} className="audit-date-group">
                                                <div className="audit-date-badge-wrapper">
                                                    <span className="audit-date-badge">{group.date_group}</span>
                                                </div>

                                                <div className="audit-timeline-items">
                                                    {group.entries.map((entry) => (
                                                        <div key={entry.log_id} className="audit-timeline-item">

                                                            {/* User avatar on the timeline path */}
                                                            <div className="audit-timeline-left">
                                                                <div className="audit-user-avatar" title={`${entry.personnel?.name} (${entry.personnel?.role})`}>
                                                                    {entry.personnel?.initials || 'S'}
                                                                </div>
                                                            </div>

                                                            {/* Activity Content Card */}
                                                            <div className="audit-timeline-right">
                                                                <div className="audit-activity-card">
                                                                    <div className="audit-card-header">
                                                                        <div className="audit-user-meta">
                                                                            <span className="audit-username">{entry.personnel?.name || 'System'}</span>
                                                                            <span className="audit-user-role">{entry.personnel?.role || 'Service'}</span>
                                                                        </div>

                                                                        <div className="audit-header-right">
                                                                            <span className="audit-timestamp">
                                                                                <Clock size={11} />
                                                                                {getLocalTime(group.date_group, entry.timestamp)}
                                                                            </span>
                                                                            <span
                                                                                className="audit-activity-badge"
                                                                                style={{
                                                                                    backgroundColor: entry.action?.badge?.bg_color || '#F3F4F6',
                                                                                    color: entry.action?.badge?.text_color || '#374151'
                                                                                }}
                                                                            >
                                                                                <ActionIcon iconName={entry.action?.icon} />
                                                                                {entry.action?.display_name || entry.action?.key || 'Event'}
                                                                            </span>
                                                                        </div>
                                                                    </div>

                                                                    <div className="audit-card-body">
                                                                        {renderTimelineBody(entry)}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="audit-empty-timeline">
                                        <Clock size={32} />
                                        <p>No logged activities found for matching criteria.</p>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AuditLogsModal;
